import React, { useState, useEffect, useMemo } from 'react';
import GameIcon from './GameIcon';

// Area visual configs
const AREA_VISUALS = {
  'main-alley':      { icon: '🏪', bg: 'bg-amber-50',   border: 'border-amber-300', accent: 'text-amber-700', ring: 'ring-amber-400' },
  'side-alley':      { icon: '🧵', bg: 'bg-teal-50',    border: 'border-teal-300',  accent: 'text-teal-700',  ring: 'ring-teal-400' },
  'hidden-alley':    { icon: '🕌', bg: 'bg-purple-50',  border: 'border-purple-300', accent: 'text-purple-700', ring: 'ring-purple-400' },
  'night-souk':      { icon: '🌙', bg: 'bg-indigo-50',  border: 'border-indigo-300', accent: 'text-indigo-700', ring: 'ring-indigo-400' },
  'rooftop-market':  { icon: '☀️', bg: 'bg-orange-50',  border: 'border-orange-300', accent: 'text-orange-700', ring: 'ring-orange-400' },
};
const DEFAULT_VISUAL = { icon: '🏪', bg: 'bg-gray-50', border: 'border-gray-300', accent: 'text-gray-700', ring: 'ring-gray-400' };

const PERSONALITY_LABELS = {
  friendly:  { label: 'Friendly',  color: 'bg-green-100 text-green-700' },
  tough:     { label: 'Tough',     color: 'bg-red-100 text-red-700' },
  tricky:    { label: 'Tricky',    color: 'bg-amber-100 text-amber-700' },
  generous:  { label: 'Generous',  color: 'bg-blue-100 text-blue-700' },
};

export default function SoukMap({
  areas, merchants, items, ownedItems, unlockedAreas, completedOutfits,
  currentMission, onSelectMerchant, onTimeCost, timerSeconds,
  walkingTimeCost = 30, gameSettings = {}
}) {
  const [walking, setWalking] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [callouts, setCallouts] = useState({});

  // Auto-expand first unlocked area on mount
  useEffect(() => {
    if (!selectedArea && areas?.length) {
      const first = areas.find(a => unlockedAreas?.includes(a.id));
      if (first) setSelectedArea(first);
    }
  }, [areas, unlockedAreas]); // eslint-disable-line

  // Random merchant callouts
  useEffect(() => {
    if (!merchants?.length) return;
    const interval = setInterval(() => {
      const m = merchants[Math.floor(Math.random() * merchants.length)];
      if (m) {
        setCallouts(prev => ({ ...prev, [m.id]: m.callout_text || m.greeting_darija || 'Aji!' }));
        setTimeout(() => setCallouts(prev => { const n = { ...prev }; delete n[m.id]; return n; }), 3000);
      }
    }, 4500);
    return () => clearInterval(interval);
  }, [merchants]);

  // ── Mission item IDs needed ──
  const missionItemIds = useMemo(() => {
    if (!currentMission?.required_items) return [];
    return currentMission.required_items.map(ri =>
      typeof ri === 'object' ? (ri.item_id || ri.id) : ri
    );
  }, [currentMission]);

  // ── Build map: merchant_id → [items needed for this mission] ──
  const merchantNeededItems = useMemo(() => {
    if (!missionItemIds.length || !items?.length) return {};
    const map = {};
    items.forEach(item => {
      if (missionItemIds.includes(item.id) && item.merchant_id) {
        if (!map[item.merchant_id]) map[item.merchant_id] = [];
        map[item.merchant_id].push(item);
      }
    });
    return map;
  }, [items, missionItemIds]);

  // Owned item set for quick lookup
  const ownedSet = useMemo(() => new Set(ownedItems || []), [ownedItems]);

  // Map merchants to areas
  const getMerchantsForArea = (area, idx) => {
    const assigned = merchants?.filter(m => m.area_id === area.id) || [];
    if (assigned.length > 0) return assigned;
    if (!merchants?.length || !areas?.length) return [];
    const perArea = Math.ceil(merchants.length / areas.length);
    return merchants.slice(idx * perArea, (idx + 1) * perArea);
  };

  // Count how many needed items are in this area (not yet owned)
  const getAreaNeededCount = (areaMerchants) => {
    let count = 0;
    areaMerchants.forEach(m => {
      const needed = merchantNeededItems[m.id];
      if (needed) {
        count += needed.filter(item => !ownedSet.has(item.id)).length;
      }
    });
    return count;
  };

  const handleAreaClick = (area) => {
    if (!unlockedAreas?.includes(area.id)) return;
    if (walking) return;

    if (selectedArea?.id === area.id) {
      setSelectedArea(null);
      return;
    }

    if (selectedArea && area.id !== selectedArea.id) {
      setWalking(area.id);
      onTimeCost?.(walkingTimeCost);
      setTimeout(() => {
        setWalking(null);
        setSelectedArea(area);
      }, 1500);
    } else {
      setSelectedArea(area);
    }
  };

  const totalUnlocked = areas?.filter(a => unlockedAreas?.includes(a.id)).length || 0;
  const totalAreas = areas?.length || 0;
  const totalNeededLeft = missionItemIds.filter(id => !ownedSet.has(id)).length;

  return (
    <div className="min-h-[calc(100vh-60px)] bg-[#F5F0E8] animate-fade-up">
      <div className="max-w-5xl mx-auto px-4 py-4 game-content">

        {/* Map Header */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-bold text-gray-900 tracking-tight">{gameSettings.map_title || 'Souk Map'}</h2>
            <span className="text-xs text-gray-400 font-medium">
              {totalUnlocked}/{totalAreas} {gameSettings.map_areas_unlocked || 'areas unlocked'}
            </span>
          </div>

          {/* Mission reminder strip */}
          {currentMission && (
            <div className="bg-white border border-gray-200 rounded-xl px-4 py-2.5 flex items-center gap-3 mt-2">
              <span className="text-lg">🎯</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-800 truncate">
                  {currentMission.name_english || currentMission.name || 'Mission'}
                </p>
                <p className="text-[10px] text-gray-500">
                  {totalNeededLeft > 0
                    ? <>{totalNeededLeft} {gameSettings.map_items_still_needed || `item${totalNeededLeft !== 1 ? 's' : ''} still needed`}</>
                    : <span className="text-green-600 font-semibold">{gameSettings.map_all_items_found || 'All items found!'}</span>
                  }
                  {currentMission.budget_limit > 0 && ` · Budget: ${currentMission.budget_limit} tokens`}
                </p>
              </div>
              {timerSeconds > 0 && (
                <div className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                  timerSeconds <= 60
                    ? 'bg-red-100 text-red-700 animate-pulse'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {Math.floor(timerSeconds / 60)}:{(timerSeconds % 60).toString().padStart(2, '0')}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Walking overlay */}
        {walking && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex items-center gap-4 animate-fade-up">
            <span className="text-3xl animate-walking"><GameIcon icon={gameSettings.icon_walking || '🚶'} /></span>
            <div>
              <p className="text-sm font-bold text-amber-800">{gameSettings.map_walking || 'Walking through the souk...'}</p>
              <p className="text-xs text-amber-600">{gameSettings.map_walking_cost ? gameSettings.map_walking_cost.replace('{seconds}', walkingTimeCost) : `-${walkingTimeCost} seconds from your time`}</p>
            </div>
            <div className="ml-auto">
              <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
            </div>
          </div>
        )}

        {/* Area list with path connectors */}
        <div className="relative">
          <div className="absolute left-6 top-6 bottom-6 w-0.5 bg-gray-200 z-0 hidden sm:block" />

          <div className="space-y-2">
            {areas.map((area, idx) => {
              const isUnlocked = unlockedAreas?.includes(area.id);
              const isSelected = selectedArea?.id === area.id;
              const vis = AREA_VISUALS[area.id] || DEFAULT_VISUAL;
              const areaMs = getMerchantsForArea(area, idx);
              const nextArea = areas[idx + 1];
              const nextUnlocked = nextArea && unlockedAreas?.includes(nextArea.id);
              const neededInArea = isUnlocked ? getAreaNeededCount(areaMs) : 0;

              return (
                <div key={area.id} className="relative">
                  {/* Path node dot */}
                  <div className={`absolute left-4 top-6 w-5 h-5 rounded-full border-2 z-10 hidden sm:flex items-center justify-center ${
                    isUnlocked
                      ? isSelected ? `${vis.border} bg-white ring-2 ${vis.ring}` : `${vis.border} bg-white`
                      : 'border-gray-300 bg-gray-200'
                  }`}>
                    {isUnlocked && isSelected && (
                      <div className={`w-2 h-2 rounded-full ${vis.bg.replace('-50', '-400')}`} />
                    )}
                  </div>

                  {/* Area card */}
                  <div className="sm:ml-14">
                    <button
                      onClick={() => handleAreaClick(area)}
                      disabled={!isUnlocked || !!walking}
                      className={`w-full text-left rounded-2xl border-2 transition-all overflow-hidden ${
                        isSelected
                          ? `${vis.border} shadow-md bg-white ring-1 ${vis.ring}`
                          : isUnlocked
                            ? neededInArea > 0
                              ? 'border-[#2D6A4F]/40 bg-white hover:shadow-sm hover:border-[#2D6A4F]/60'
                              : 'border-gray-200 bg-white hover:shadow-sm hover:border-gray-300'
                            : 'border-gray-200 bg-gray-100/80 cursor-not-allowed'
                      }`}
                    >
                      <div className="p-4 flex items-center gap-3">
                        {/* Area icon circle */}
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 ${
                          isUnlocked ? vis.bg : 'bg-gray-200'
                        }`}>
                          <GameIcon icon={isUnlocked ? (area.icon || gameSettings[`icon_area_${area.id?.replace(/-/g, '_')}`] || vis.icon) : (gameSettings.icon_locked || '🔒')} />
                        </div>

                        {/* Area info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className={`font-bold text-sm ${isUnlocked ? 'text-gray-900' : 'text-gray-400'}`}>
                              {area.name}
                            </h3>
                            {area.name_darija && isUnlocked && (
                              <span className="text-[11px] text-[#2D6A4F] font-medium bg-[#2D6A4F]/5 px-1.5 py-0.5 rounded">
                                {area.name_darija}
                              </span>
                            )}
                            {/* Needed items badge on area card */}
                            {isUnlocked && neededInArea > 0 && (
                              <span className="text-[9px] font-bold text-white bg-[#2D6A4F] px-2 py-0.5 rounded-full">
                                {neededInArea} {gameSettings.map_items_you_need || `item${neededInArea !== 1 ? 's' : ''} you need`}
                              </span>
                            )}
                          </div>
                          {isUnlocked ? (
                            <p className="text-xs text-gray-500 mt-0.5">
                              {areaMs.length} {gameSettings.map_merchants_label || `merchant${areaMs.length !== 1 ? 's' : ''}`}
                              {area.description && ` — ${area.description}`}
                            </p>
                          ) : (
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gray-400 rounded-full transition-all"
                                  style={{ width: `${Math.min(100, ((completedOutfits?.length || 0) / (area.unlock_requirement || 1)) * 100)}%` }}
                                />
                              </div>
                              <span className="text-[10px] text-gray-400 flex-shrink-0">
                                {completedOutfits?.length || 0}/{area.unlock_requirement} {gameSettings.map_missions_label || 'missions'}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Expand chevron or lock */}
                        {isUnlocked ? (
                          <svg
                            className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${isSelected ? 'rotate-180' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        ) : (
                          <span className="text-gray-300 text-lg flex-shrink-0"><GameIcon icon={gameSettings.icon_locked || '🔒'} /></span>
                        )}
                      </div>
                    </button>

                    {/* Expanded merchant list */}
                    {isSelected && isUnlocked && (
                      <div className="mt-2 space-y-2 animate-fade-up pb-2">
                        {areaMs.map(merchant => {
                          const neededItems = merchantNeededItems[merchant.id] || [];
                          const stillNeeded = neededItems.filter(item => !ownedSet.has(item.id));
                          const alreadyOwned = neededItems.filter(item => ownedSet.has(item.id));
                          const hasNeeded = stillNeeded.length > 0;
                          const personality = PERSONALITY_LABELS[merchant.personality];

                          return (
                            <button
                              key={merchant.id}
                              onClick={() => onSelectMerchant(merchant)}
                              className={`w-full bg-white rounded-xl border p-4 hover:shadow-md transition-all text-left group ${
                                hasNeeded
                                  ? 'border-[#2D6A4F] ring-1 ring-[#2D6A4F]/20'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {/* Merchant avatar */}
                                <div className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl flex-shrink-0 overflow-hidden border-2 ${
                                  hasNeeded ? 'border-[#2D6A4F] bg-[#2D6A4F]/5' : 'border-gray-200 bg-[#F5F0E8]'
                                }`}>
                                  {merchant.image_url && (merchant.image_url.startsWith('http') || merchant.image_url.startsWith('/')) ? (
                                    <img src={merchant.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                                  ) : (
                                    <span><GameIcon icon={merchant.image_url && !merchant.image_url.startsWith('/') ? merchant.image_url : (gameSettings.icon_default_merchant || '🧔')} /></span>
                                  )}
                                </div>

                                {/* Merchant info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="font-bold text-gray-900 text-sm group-hover:text-[#2D6A4F] transition-colors">
                                      {merchant.name || merchant.name_english || 'Merchant'}
                                    </h4>
                                    {personality && (
                                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${personality.color}`}>
                                        {personality.label}
                                      </span>
                                    )}
                                  </div>

                                  {merchant.specialty && (
                                    <p className="text-[10px] text-[#C84B31] font-medium mt-0.5">{merchant.specialty}</p>
                                  )}

                                  <p className="text-xs text-gray-500 italic mt-1 line-clamp-1">
                                    "{merchant.greeting_darija || merchant.description || 'Marhba!'}"
                                  </p>

                                  {/* Needed items list */}
                                  {(stillNeeded.length > 0 || alreadyOwned.length > 0) && (
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                      {stillNeeded.map(item => (
                                        <span
                                          key={item.id}
                                          className="text-[10px] font-semibold text-[#2D6A4F] bg-[#2D6A4F]/10 px-2 py-0.5 rounded-full"
                                        >
                                          {item.name_english || item.name_darija || 'Item'}
                                        </span>
                                      ))}
                                      {alreadyOwned.map(item => (
                                        <span
                                          key={item.id}
                                          className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full line-through"
                                        >
                                          {item.name_english || item.name_darija || 'Item'}
                                        </span>
                                      ))}
                                    </div>
                                  )}

                                  {/* Callout bubble */}
                                  {callouts[merchant.id] && (
                                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 text-xs text-amber-800 animate-callout inline-block">
                                      "{callouts[merchant.id]}"
                                    </div>
                                  )}
                                </div>

                                {/* Visit arrow */}
                                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                    hasNeeded
                                      ? 'bg-[#2D6A4F] text-white'
                                      : 'bg-gray-100 text-gray-400 group-hover:bg-[#2D6A4F] group-hover:text-white'
                                  }`}>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </div>
                                  <span className="text-[8px] text-gray-400">{gameSettings.map_visit_button || 'Visit'}</span>
                                </div>
                              </div>
                            </button>
                          );
                        })}

                        {areaMs.length === 0 && (
                          <div className="text-center py-8 text-gray-400 text-sm bg-white rounded-xl border border-gray-200">
                            <span className="text-2xl block mb-2">🏗️</span>
                            {gameSettings.map_no_merchants || 'No merchants in this area yet'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Path connector */}
                  {idx < areas.length - 1 && (
                    <div className="hidden sm:flex items-center justify-start ml-5 my-1">
                      <div className={`w-0.5 h-3 ${
                        isUnlocked && nextUnlocked ? 'bg-gray-300' : 'bg-gray-200'
                      }`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom hint */}
        <div className="mt-6 text-center text-[10px] text-gray-400">
          {gameSettings.map_walking_hint ? gameSettings.map_walking_hint.replace('{seconds}', walkingTimeCost) : `Walking between areas costs ${walkingTimeCost} seconds — choose wisely!`}
        </div>
      </div>
    </div>
  );
}
